import {useMapsLibrary} from "@vis.gl/react-google-maps";
import {useEffect, useRef, useState} from "react";
import type {Location} from "@/api/apiClient";
import {MAP_CONFIG} from "../utils/constants";
import {toLatLng} from "../utils/coordinates";
import type {LatLng, SheetOffset} from "../utils/types";

const MARKER_HALF_WIDTH_PX = 90; // Marker half-width in pixels (from CSS: max-width 160px + padding 20px = 180px total, half = 90px)
const MARKER_HEIGHT_PX = 56; // Marker height in pixels (42px box + 14px leg, from LocationMarker.module.css)
const SHEET_LEFT_PADDING_PX = 20; // Left padding of floatingWrapper in landscape mode (from Order.module.css)

type UseMapPositionProps = {
    map: google.maps.Map | null;
    pickup?: Location;
    dropoff?: Location;
    rideShape?: string;
    defaultCenter: LatLng;
    sheetOffset: SheetOffset;
    isPortrait: boolean;
};

type AvailableRect = {
    width: number;
    height: number;
    centerX: number;
    centerY: number;
};

/**
 * Calculate available rectangle in the viewport (excluding sheet).
 * Portrait: available area is above the sheet (at bottom).
 * Landscape: available area is to the right of the sheet (on left).
 */
function getAvailableRect(
    viewportWidth: number,
    viewportHeight: number,
    sheetOffset: SheetOffset,
    isPortrait: boolean,
    padding: number
): AvailableRect {
    if (isPortrait) {
        const availableHeight = viewportHeight - sheetOffset.height;
        return {
            width: viewportWidth - padding * 2,
            height: availableHeight - padding * 2,
            centerX: viewportWidth / 2,
            centerY: availableHeight / 2,
        };
    } else {
        // Include sheet wrapper's left padding (not measured in sheetOffset.width)
        const totalSheetWidth = sheetOffset.width + SHEET_LEFT_PADDING_PX;
        const availableWidth = viewportWidth - totalSheetWidth;
        return {
            width: availableWidth - padding * 2,
            height: viewportHeight - padding * 2,
            centerX: totalSheetWidth + availableWidth / 2,
            centerY: viewportHeight / 2,
        };
    }
}

/**
 * Find optimal zoom level where bounds + marker padding fit in available rect.
 * Uses binary search to find the highest zoom where content fits.
 */
function findOptimalZoom(
    projection: google.maps.Projection,
    bounds: google.maps.LatLngBounds,
    availableRect: AvailableRect,
    markerHalfWidthPx: number,
    markerHeightPx: number
): number {
    const ne = projection.fromLatLngToPoint(bounds.getNorthEast());
    const sw = projection.fromLatLngToPoint(bounds.getSouthWest());
    if (!ne || !sw) return MAP_CONFIG.DEFAULT_ZOOM;

    const boundsWorldWidth = ne.x - sw.x;
    const boundsWorldHeight = sw.y - ne.y;

    let minZoom: number = 1;
    let maxZoom: number = MAP_CONFIG.MAX_ZOOM;

    while (maxZoom - minZoom > 0.01) {
        const testZoom = (minZoom + maxZoom) / 2;
        const scale = Math.pow(2, testZoom);
        // Content width includes marker padding on both sides
        const contentWidthPx = boundsWorldWidth * scale + markerHalfWidthPx * 2;
        // Content height includes marker height at the top
        const contentHeightPx = boundsWorldHeight * scale + markerHeightPx;

        if (contentWidthPx <= availableRect.width && contentHeightPx <= availableRect.height) {
            minZoom = testZoom;
        } else {
            maxZoom = testZoom;
        }
    }

    return Math.floor(minZoom);
}

/**
 * Calculate the map center that positions bounds center at available rect center.
 * We shift map center in opposite direction of the offset (to make content appear at availableRect center).
 */
function calculateAdjustedCenter(
    projection: google.maps.Projection,
    boundsCenter: google.maps.LatLng,
    zoom: number,
    viewportWidth: number,
    viewportHeight: number,
    availableRect: AvailableRect
): google.maps.LatLngLiteral {
    const scale = Math.pow(2, zoom);
    const centerWorld = projection.fromLatLngToPoint(boundsCenter);
    if (!centerWorld) return {lat: boundsCenter.lat(), lng: boundsCenter.lng()};

    const offsetPxX = availableRect.centerX - viewportWidth / 2;
    const offsetPxY = availableRect.centerY - viewportHeight / 2;

    const newCenterWorld = new google.maps.Point(
        centerWorld.x - offsetPxX / scale,
        centerWorld.y - offsetPxY / scale
    );

    const newCenter = projection.fromPointToLatLng(newCenterWorld);
    if (!newCenter) return {lat: boundsCenter.lat(), lng: boundsCenter.lng()};

    return {lat: newCenter.lat(), lng: newCenter.lng()};
}

function doPositionMap(
    map: google.maps.Map,
    geometry: google.maps.GeometryLibrary,
    pickup: Location | undefined,
    dropoff: Location | undefined,
    rideShape: string | undefined,
    sheetOffset: SheetOffset,
    isPortrait: boolean,
    defaultCenter: LatLng
): void {
    // Wait for projection to be available
    const projection = map.getProjection();
    if (!projection) return;
    const isSheetMeasured = isPortrait ? sheetOffset.height > 0 : sheetOffset.width > 0;
    if (!isSheetMeasured) return;

    const div = map.getDiv();
    const viewportWidth = div.offsetWidth;
    const viewportHeight = div.offsetHeight;

    const availableRect = getAvailableRect(
        viewportWidth,
        viewportHeight,
        sheetOffset,
        isPortrait,
        MAP_CONFIG.PADDING
    );

    const hasPickup = pickup?.lat != null && pickup?.lon != null;
    const hasDropoff = dropoff?.lat != null && dropoff?.lon != null;

    if (hasPickup && hasDropoff) {
        // Both locations - fit route in available rectangle
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(toLatLng(pickup));
        bounds.extend(toLatLng(dropoff));
        if (rideShape) {
            geometry.encoding.decodePath(rideShape).forEach((p) => bounds.extend(p));
        }

        const optimalZoom = findOptimalZoom(
            projection,
            bounds,
            availableRect,
            MARKER_HALF_WIDTH_PX,
            MARKER_HEIGHT_PX
        );
        const adjustedCenter = calculateAdjustedCenter(
            projection,
            bounds.getCenter(),
            optimalZoom,
            viewportWidth,
            viewportHeight,
            availableRect
        );

        map.setZoom(optimalZoom);
        map.panTo(adjustedCenter);
    } else if (hasPickup) {
        const adjustedCenter = calculateAdjustedCenter(
            projection,
            new google.maps.LatLng(toLatLng(pickup)),
            MAP_CONFIG.LOCATION_ZOOM,
            viewportWidth,
            viewportHeight,
            availableRect
        );
        map.setZoom(MAP_CONFIG.LOCATION_ZOOM);
        map.panTo(adjustedCenter);
    } else if (hasDropoff) {
        // Only dropoff - center on it
        const adjustedCenter = calculateAdjustedCenter(
            projection,
            new google.maps.LatLng(toLatLng(dropoff)),
            MAP_CONFIG.LOCATION_ZOOM,
            viewportWidth,
            viewportHeight,
            availableRect
        );
        map.setZoom(MAP_CONFIG.LOCATION_ZOOM);
        map.panTo(adjustedCenter);
    } else if (defaultCenter) {
        // No locations - use default center
        const adjustedCenter = calculateAdjustedCenter(
            projection,
            new google.maps.LatLng(defaultCenter),
            MAP_CONFIG.DEFAULT_ZOOM,
            viewportWidth,
            viewportHeight,
            availableRect
        );

        map.setZoom(MAP_CONFIG.DEFAULT_ZOOM);
        map.panTo(adjustedCenter);
    }
}

export function useMapPosition({
    map,
    pickup,
    dropoff,
    rideShape,
    defaultCenter,
    sheetOffset,
    isPortrait,
}: UseMapPositionProps) {
    const geometry = useMapsLibrary("geometry");
    const [showResetButton, setShowResetButton] = useState(false);

    // For reset button - uses current values from props
    const resetMapPosition = () => {
        if (!map || !geometry) return;
        doPositionMap(
            map,
            geometry,
            pickup,
            dropoff,
            rideShape,
            sheetOffset,
            isPortrait,
            defaultCenter
        );
        setShowResetButton(false);
    };

    useEffect(() => {
        if (!map || !geometry) return;

        const projection = map.getProjection();
        if (projection) {
            doPositionMap(
                map,
                geometry,
                pickup,
                dropoff,
                rideShape,
                sheetOffset,
                isPortrait,
                defaultCenter
            );
        } else {
            // Wait for projection to be ready
            const listener = google.maps.event.addListenerOnce(map, "idle", () => {
                doPositionMap(
                    map,
                    geometry,
                    pickup,
                    dropoff,
                    rideShape,
                    sheetOffset,
                    isPortrait,
                    defaultCenter
                );
            });
            return () => google.maps.event.removeListener(listener);
        }
    }, [map, geometry, pickup, dropoff, rideShape, sheetOffset, isPortrait, defaultCenter]);

    const prevSheetMeasuredRef = useRef(false);
    useEffect(() => {
        const isSheetMeasured = isPortrait ? sheetOffset.height > 0 : sheetOffset.width > 0;
        const wasNotMeasured = !prevSheetMeasuredRef.current;

        if (isSheetMeasured && wasNotMeasured && map && geometry) {
            doPositionMap(
                map,
                geometry,
                pickup,
                dropoff,
                rideShape,
                sheetOffset,
                isPortrait,
                defaultCenter
            );
        }

        prevSheetMeasuredRef.current = isSheetMeasured;
    }, [map, geometry, pickup, dropoff, rideShape, sheetOffset, isPortrait, defaultCenter]);

    // Show reset button when user manually pans the map
    useEffect(() => {
        if (!map) return;
        const listener = map.addListener("center_changed", () => setShowResetButton(true));
        return () => google.maps.event.removeListener(listener);
    }, [map]);

    return {showResetButton, resetMapPosition};
}
